// Daily areas from an earlier day that nobody has expired (Oct 23, 2026).
//
// Nothing expires by itself — Greg: "give james or the manager the ability to
// expire an area and create a new area each day." So the Today tab points at
// yesterday's areas still open and offers the two things James does with
// them: Expire (ends it for everyone) or Carry to today (same polygon and
// people as a fresh area for today; yesterday's numbers stay with yesterday).

import { useState } from "react";
import { areaName, carryArea, expireArea, firstName, type AreaStats } from "../../../lib/canvasAreas";
import { openPastAreas } from "../../../lib/dailyAreas";
import { dayLabel } from "../../../lib/dailyCommitments";

export default function OpenPastAreas({ areas, day, canWrite, onChanged }: {
  areas: AreaStats[] | null;
  day: string;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const past = openPastAreas(areas ?? [], day);
  if (past.length === 0) return null;

  const dayWord = dayLabel(day, { weekday: "long" });

  async function expire(a: AreaStats) {
    if (!window.confirm(`Expire ${areaName(a)}? It comes off everyone's map and its pins go back to the open pool.`)) return;
    setBusy(a.area_group_id); setMsg(null);
    try { await expireArea(a.area_group_id); setMsg(`${areaName(a)} expired.`); onChanged(); }
    catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function carry(a: AreaStats) {
    setBusy(a.area_group_id); setMsg(null);
    try {
      const r = await carryArea(a.area_group_id, day);
      setMsg(`${areaName(a)} carried to ${dayWord} for ${r.reps} ${r.reps === 1 ? "person" : "people"}.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function expireAll() {
    if (!window.confirm(`Expire all ${past.length} areas from earlier days?`)) return;
    setBusy("all"); setMsg(null);
    try {
      for (const a of past) await expireArea(a.area_group_id);
      setMsg(`${past.length} areas expired.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
          {past.length === 1 ? "An area" : `${past.length} areas`} from earlier {past.length === 1 ? "is" : "are"} still open
        </div>
        <div className="text-[11px] text-amber-800/80">Daily areas stay until someone expires them.</div>
        {canWrite && past.length > 1 && (
          <button type="button" onClick={() => void expireAll()} disabled={busy !== null}
            className="ml-auto rounded-md border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50">
            {busy === "all" ? "Expiring…" : "Expire all"}
          </button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {past.map((a) => (
          <div key={a.area_group_id} className="inline-flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs">
            <span className="font-bold text-slate-900">{areaName(a)}</span>
            <span className="text-slate-500">
              for {dayLabel(a.for_date!, { weekday: "short", month: "short", day: "numeric" })}
              {a.reps.length ? ` · ${a.reps.map((r) => firstName(r.full_name)).join(", ")}` : ""}
              {` · ${a.activity.knocks_since} ${a.activity.knocks_since === 1 ? "knock" : "knocks"}`}
            </span>
            {canWrite && (
              <>
                <button type="button" onClick={() => void expire(a)} disabled={busy !== null}
                  className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {busy === a.area_group_id ? "…" : "Expire"}
                </button>
                <button type="button" onClick={() => void carry(a)} disabled={busy !== null}
                  className="rounded-md bg-orange-500 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-orange-600 disabled:opacity-50">
                  Carry to {dayWord}
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {msg && <div className="mt-2 rounded-md bg-white px-2 py-1.5 text-xs text-slate-700 ring-1 ring-amber-200">{msg}</div>}
    </div>
  );
}
